'use client';

import type { PrV2CheckRunResponse } from '@/lib/pr-v2/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { fetchPrV2Checks } from './fetch-pr-v2';
import { prV2ChecksQueryKey } from './query-keys';

// Checks can update rapidly while CI runs, but a short cache window avoids redundant refetches.
const PR_V2_CHECKS_STALE_TIME_MS = 30_000;

export function usePrV2Checks(
  owner: string,
  repo: string,
  number: string,
  options?: {
    enabled?: boolean;
    initialData?: PrV2CheckRunResponse[];
    currentHeadSha?: string | null;
  },
) {
  const head = options?.currentHeadSha;
  const previousHead = useRef(head);
  const enabled = Boolean(owner && repo && number) && (options?.enabled ?? true);
  const query = useQuery({
    queryKey: prV2ChecksQueryKey(owner, repo, number),
    queryFn: ({ signal }) => fetchPrV2Checks(owner, repo, number, signal),
    enabled,
    initialData: options?.initialData,
    staleTime: PR_V2_CHECKS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const { refetch } = query;
  useEffect(() => {
    if (!enabled || !head) {
      return;
    }
    const changed = previousHead.current !== head;
    previousHead.current = head;
    if (changed) {
      void refetch();
    }
  }, [enabled, head, refetch]);
  return query;
}
